import React from 'react';
import { Globe, ShieldCheck, ChevronRight, Server, Cpu, ExternalLink } from 'lucide-react';
import type { XCloudSite } from '@/types/xcloud';

interface SiteRowProps {
  site: XCloudSite;
  onClick: () => void;
  view?: 'list' | 'grid';
}

function buildUrl(site: XCloudSite): string {
  const raw = site.domain_name || site.domain || site.name || '';
  if (!raw) return '';
  return raw.startsWith('http') ? raw : `https://${raw}`;
}

function getSiteStatusLabel(status: string): string {
  if (status === 'active' || status === 'provisioned') return 'Active';
  if (status === 'inactive') return 'Inactive';
  if (status === 'provisioning') return 'Provisioning';
  if (status === 'failed') return 'Failed';
  return status.charAt(0).toUpperCase() + status.slice(1);
}

function StatusPill({ status }: { status: string }) {
  const pillMap: Record<string, string> = {
    active: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
    provisioned: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
    inactive: 'bg-muted text-muted-foreground',
    provisioning: 'bg-sky-500/10 text-sky-600 dark:text-sky-400',
    failed: 'bg-red-500/10 text-red-600 dark:text-red-400',
  };
  const dotMap: Record<string, string> = {
    active: 'bg-emerald-500',
    provisioned: 'bg-emerald-500',
    inactive: 'bg-muted-foreground',
    provisioning: 'bg-sky-500',
    failed: 'bg-red-500',
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${pillMap[status] ?? 'bg-muted text-muted-foreground'}`}>
      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${dotMap[status] ?? 'bg-muted-foreground'}`} />
      {getSiteStatusLabel(status)}
    </span>
  );
}

export function SiteRow({ site, onClick, view = 'list' }: SiteRowProps) {
  const siteTitle = site.title || site.name || site.domain_name || site.domain || 'Untitled site';
  const siteUrl = site.domain_name || site.domain || (site.title ? site.name : undefined) || '';
  const displayUrl = siteUrl.replace(/^https?:\/\//, '');
  const fullUrl = buildUrl(site);
  const sslActive = site.ssl?.status === 'active';

  const handleUrlClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (fullUrl) window.open(fullUrl, '_blank', 'noopener,noreferrer');
  };

  if (view === 'grid') {
    return (
      <button
        onClick={onClick}
        className="w-full text-left group rounded-xl border border-border bg-white dark:bg-card hover:border-sky-500/40 hover:shadow-sm transition-all p-5"
      >
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="w-10 h-10 rounded-lg bg-sky-500/10 flex items-center justify-center shrink-0">
            <Globe className="w-5 h-5 text-sky-500" />
          </div>
          <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors shrink-0 mt-1" />
        </div>

        <p className="font-semibold text-sm text-foreground truncate mb-0.5">{siteTitle}</p>
        {displayUrl && (
          <span
            role="link"
            onClick={handleUrlClick}
            className="inline-flex items-center gap-1 text-xs text-sky-500 hover:text-sky-400 transition-colors truncate mb-2 group/url cursor-pointer"
          >
            <span className="truncate">{displayUrl}</span>
            <ExternalLink className="w-3 h-3 shrink-0 opacity-0 group-hover/url:opacity-100 transition-opacity" />
          </span>
        )}

        <div className="flex items-center gap-2 flex-wrap">
          <StatusPill status={site.status} />
          {sslActive && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
              <ShieldCheck className="w-3 h-3" />
              SSL
            </span>
          )}
        </div>

        <div className="mt-3 pt-3 border-t border-border grid grid-cols-2 gap-2">
          {site.php_version && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <span className="text-sky-500/70 font-semibold text-[10px] shrink-0">PHP</span>
              <span className="truncate">{site.php_version}</span>
            </div>
          )}
          {site.type && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Cpu className="w-3 h-3 text-sky-500/70 shrink-0" />
              <span className="truncate capitalize">{site.type.replace(/-/g, ' ')}</span>
            </div>
          )}
          {site.server_name && (
            <div className="col-span-2 flex items-center gap-1.5 text-xs text-muted-foreground">
              <Server className="w-3 h-3 text-sky-500/70 shrink-0" />
              <span className="truncate">{site.server_name}</span>
            </div>
          )}
        </div>
      </button>
    );
  }

  // List view
  return (
    <button
      onClick={onClick}
      className="w-full text-left group flex items-center gap-3 px-4 py-3.5 rounded-xl border border-border bg-white dark:bg-card hover:border-sky-500/40 hover:bg-muted/30 transition-all"
    >
      <div className="w-8 h-8 rounded-lg bg-sky-500/10 flex items-center justify-center shrink-0">
        <Globe className="w-4 h-4 text-sky-500" />
      </div>

      {/* Left: title + url */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm text-foreground truncate">{siteTitle}</span>
          <StatusPill status={site.status} />
          {sslActive && (
            <span className="inline-flex items-center gap-0.5 text-xs text-emerald-500 font-medium shrink-0">
              <ShieldCheck className="w-3.5 h-3.5" />
              SSL
            </span>
          )}
        </div>
        {displayUrl && (
          <span
            role="link"
            onClick={handleUrlClick}
            className="inline-flex items-center gap-1 mt-0.5 text-xs text-sky-500 hover:text-sky-400 transition-colors group/url cursor-pointer"
          >
            <span className="truncate max-w-xs">{displayUrl}</span>
            <ExternalLink className="w-3 h-3 shrink-0 opacity-0 group-hover/url:opacity-100 transition-opacity" />
          </span>
        )}
      </div>

      {/* Right: meta tags */}
      <div className="flex items-center gap-2 shrink-0">
        {site.php_version && (
          <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-md font-mono">
            PHP {site.php_version}
          </span>
        )}
        {site.type && (
          <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-md capitalize">
            {site.type.replace(/-/g, ' ')}
          </span>
        )}
      </div>

      <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors shrink-0" />
    </button>
  );
}
